import pino, { type Logger, type LoggerOptions, type Level as PinoLevel } from 'pino';
import { getLoggerConfig, getLoggerGeneration } from './config';
import { originalConsoleMethods } from './console-interceptor';
import { sendToDatadog } from './datadog';
import { pinoRedactedPaths } from './redaction';
import {
  type ChildLoggerBindings,
  type CustomLogger,
  type LogContext,
  type LoggerScope,
} from './types';

const shouldSendToDatadog = (level: PinoLevel): boolean => {
  const { isProd } = getLoggerConfig();
  if (!isProd) return true; // send all levels in non-prod

  // in prod, only send warn, error, fatal
  const levelHierarchy: Record<PinoLevel, number> = {
    trace: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
    fatal: 5,
  };

  return levelHierarchy[level] >= levelHierarchy['info'];
};

// Would this record reach ANY sink? Datadog first (the cheap config read),
// then pino — whose level is `silent` in prod, so a prod build with the
// on-prem gate closed answers `false` for every level.
const willEmit = (level: PinoLevel, pinoLogger: Logger): boolean => {
  const { enabled } = getLoggerConfig();
  if (enabled && shouldSendToDatadog(level)) return true;
  return pinoLogger.isLevelEnabled(level);
};

// The ambient-context seam (`configureLogger({ enrich })`). It runs on every
// emitted record, so it is skipped when nothing would be emitted — an
// enricher that reads live state must not be charged to a `logger.debug()`
// that goes nowhere.
const collectAmbientContext = (level: PinoLevel, pinoLogger: Logger): LogContext => {
  const { enrich } = getLoggerConfig();
  if (enrich === undefined || !willEmit(level, pinoLogger)) return {};

  try {
    return enrich() ?? {};
  } catch {
    // A throwing enricher must never take down the call site: drop the
    // ambient context, still log the record. Reporting the failure through
    // this same logger would risk recursing straight back into `enrich`.
    return {};
  }
};

// This creates the base Pino logger from the injected config (G1/F5 — the
// env/version/prod signals arrive via configureLogger, never process.env).
const createBaseLogger = (): Logger => {
  const { isProd, environment, release } = getLoggerConfig();

  const options: LoggerOptions = {
    level: isProd ? 'silent' : 'debug',
    base: {
      env: environment,
      version: release,
    },
    redact: {
      paths: pinoRedactedPaths,
      censor: '[REDACTED]',
    },
    browser: {
      asObject: true,
      write: isProd
        ? undefined
        : {
            debug: (o) => originalConsoleMethods.debug(o),
            info: (o) => originalConsoleMethods.info(o),
            warn: (o) => originalConsoleMethods.warn(o),
            error: (o) => originalConsoleMethods.error(o),
          },
      transmit: {
        level: isProd ? 'silent' : 'debug',
        send: () => {},
      },
    },
  };

  return pino(options);
};

// The pino instances are cached per configure-generation: the exported
// `logger` singleton (and any child) is a stable object whose methods read
// the CURRENT configuration, so consumers that captured it at module scope —
// before configureLogger() ran — pick up the injected config on their next
// log call instead of holding a stale pre-config pino.
let cachedBase: Logger | null = null;
let cachedBaseGeneration = -1;

const getBaseLogger = (): Logger => {
  const generation = getLoggerGeneration();
  if (cachedBase === null || cachedBaseGeneration !== generation) {
    cachedBase = createBaseLogger();
    cachedBaseGeneration = generation;
  }
  return cachedBase;
};

// This wraps the base Pino logger so we can send everything to Datadog / anything else we may want in future
const createCustomLogger = (bindings: LogContext = {}): CustomLogger => {
  let cachedPino: Logger | null = null;
  let cachedGeneration = -1;

  const getPino = (): Logger => {
    const generation = getLoggerGeneration();
    if (cachedPino === null || cachedGeneration !== generation) {
      const base = getBaseLogger();
      cachedPino = Object.keys(bindings).length > 0 ? base.child(bindings) : base;
      cachedGeneration = generation;
    }
    return cachedPino;
  };

  const createLogMethod = (level: PinoLevel) => {
    return (...args: unknown[]): void => {
      const pinoLogger = getPino();
      const message = args.length > 0 ? args[0] : undefined;
      // This is how both Pino and Datadog expect it to be formatted.
      // Ambient context goes first: call-site bindings outrank it on a key
      // collision, so a `child({ domain })` tag can never be clobbered by a
      // global enricher.
      const context: LogContext = {
        ...collectAmbientContext(level, pinoLogger),
        ...(args.length > 1 ? { additionalArgs: args.slice(1) } : {}),
        ...bindings,
      };

      if ((level === 'error' || level === 'fatal') && message instanceof Error) {
        pinoLogger[level]?.({ ...context, stack: message.stack }, message.message);
      } else if (
        message !== null &&
        message !== undefined &&
        typeof message === 'object'
      ) {
        pinoLogger[level]?.({ ...context, ...(message as Record<string, unknown>) });
      } else {
        pinoLogger[level]?.(
          context,
          message !== undefined ? JSON.stringify(message) : ''
        );
      }
      if (shouldSendToDatadog(level)) {
        sendToDatadog(level, message, context);
      }
    };
  };

  const customLogger: CustomLogger = {
    trace: createLogMethod('trace'),
    debug: createLogMethod('debug'),
    info: createLogMethod('info'),
    warn: createLogMethod('warn'),
    error: createLogMethod('error'),
    fatal: createLogMethod('fatal'),
    log: (...args: unknown[]): void => {
      // 'log' is aliased to 'debug'
      customLogger.debug(...args);
    },
    child: (newBindings: ChildLoggerBindings): CustomLogger => {
      return createCustomLogger({ ...bindings, ...newBindings });
    },
  };

  return customLogger;
};

export const logger: CustomLogger = createCustomLogger();

/**
 * Create one logger with its required observability scope. Product modules own
 * concrete instances; Runtime owns the composition mechanism. Use `child()` to
 * add narrower static module bindings.
 */
export function createLogger(scope: LoggerScope): CustomLogger {
  return createCustomLogger({ ...scope });
}
